import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { refreshCaptureModes, useCaptureModes, type CaptureMode } from '../lib/captureModes';

/**
 * ═══ HOW THIS SCHOOL TAKES ITS REGISTER ═══
 *
 * The switches behind the attendance screens, per institution.
 *
 * ⚠ THIS SCREEN EXISTED NOWHERE BEFORE. The attendance POLICY endpoint — the eligible threshold,
 * the condonation floor, the shortage percentage, the teaching days — has been in the API since the
 * beginning with no way to reach it from the product. Those numbers decide who is reported short of
 * attendance, so they were being run at whatever the defaults happened to be. They are on this page
 * because this is now the page about attendance settings, and a second one would be worse.
 *
 * ⚠ PER INSTITUTION, NOT PER TENANT. A trust runs the photo roll in the primary school and the
 * scanner in the senior school; that is the normal case, not the exotic one. The menu shows the
 * union — anything any institution uses — and each screen asks which institution once you are in it.
 */

interface Institution { id: string; name: string; isActive: boolean }

interface Policy {
  institutionId: string;
  eligibleThresholdPercent: number;
  condonationFloorPercent: number;
  shortagePercent: number;
  teachingDaysMask: number;
  captureModes: CaptureMode[];
  faceRequiresParentConsent: boolean;
  availableCaptureModes: CaptureMode[];
}

/** Monday = bit 0 … Sunday = bit 6, matching AttendancePolicy.IsTeachingDay on the server. */
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MODE_TEXT: Record<CaptureMode, { label: string; blurb: string; needs?: string; to?: string }> = {
  NameList: {
    label: 'Name list',
    blurb: 'The plain register: a list of names, a control each.',
  },
  PhotoRoll: {
    label: 'Photo roll',
    blurb: 'A grid of faces. Everyone starts present; the teacher taps whoever is not there.',
    needs: 'Needs student photographs',
    to: 'Students → Photographs',
  },
  CardScan: {
    label: 'Card scan',
    blurb: 'The teacher’s phone reads the QR on each ID card at the door.',
    needs: 'Needs printed ID cards',
    to: 'Students → ID cards',
  },
  Face: {
    label: 'Face recognition',
    blurb: 'The phone recognises the child in front of it and marks them present.',
  },
};

export function AttendanceSettingsPage() {
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [institutionId, setInstitutionId] = useState('');
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [modes, setModes] = useState<CaptureMode[]>([]);
  const [eligible, setEligible] = useState(85);
  const [floor, setFloor] = useState(75);
  const [shortage, setShortage] = useState(85);
  const [days, setDays] = useState(0b0111111);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const capture = useCaptureModes();

  useEffect(() => {
    api.get<Institution[]>('/api/institutions')
      .then((xs) => { setInstitutions(xs); setInstitutionId((i) => i || xs[0]?.id || ''); })
      .catch(() => setInstitutions([]));
  }, []);

  const load = useCallback(async () => {
    if (!institutionId) return;
    setBusy(true); setError(null); setSaved(false);
    try {
      const p = await api.get<Policy>(`/api/attendance/policy/${institutionId}`);
      setPolicy(p);
      setModes(p.captureModes);
      setEligible(p.eligibleThresholdPercent);
      setFloor(p.condonationFloorPercent);
      setShortage(p.shortagePercent);
      setDays(p.teachingDaysMask);
    } catch (e) {
      setPolicy(null);
      setError(e instanceof ApiError ? e.message : 'Could not load the attendance settings.');
    } finally { setBusy(false); }
  }, [institutionId]);

  useEffect(() => { void load(); }, [load]);

  /**
   * ⚠ THE LIST OF MODES COMES FROM THE SERVER, not from this bundle. A browser holding a cached
   * build must not offer a mode this server has never heard of — it would save, come back empty,
   * and look like the switch did not stick.
   */
  const available: CaptureMode[] = policy?.availableCaptureModes?.length
    ? policy.availableCaptureModes
    : (capture.available ?? []);

  const toggle = (m: CaptureMode) => {
    setSaved(false);
    setModes((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]));
  };

  // ⚠ Checked client-side because the server throws a domain rule violation, and a red API error
  // after pressing Save is a worse way to learn this than the sentence under the field.
  const floorTooHigh = floor > eligible;

  const dayOn = (i: number) => (days & (1 << i)) !== 0;
  const toggleDay = (i: number) => { setSaved(false); setDays((d) => d ^ (1 << i)); };

  const save = async () => {
    if (floorTooHigh) return;
    setBusy(true); setError(null);
    try {
      const p = await api.put<Policy>(`/api/attendance/policy/${institutionId}`, {
        eligibleThresholdPercent: eligible,
        condonationFloorPercent: floor,
        shortagePercent: shortage,
        teachingDaysMask: days,
        captureModes: modes,
        // ⚠ Always true. There is no lawful version of face recognition on children without
        // per-child consent, so this is not a switch — it is stated, and sent.
        faceRequiresParentConsent: true,
      });
      setPolicy(p);
      setModes(p.captureModes);
      setSaved(true);
      // ⚠ The menu is drawn from a cached answer. Without this, a school switches the scanner on,
      // presses Save, and the entry it was switching on does not appear until a reload.
      refreshCaptureModes();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the attendance settings.');
    } finally { setBusy(false); }
  };

  const others = useMemo(
    () => capture.institutions.filter((i) => i.institutionId !== institutionId),
    [capture.institutions, institutionId]);

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Attendance</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Attendance settings</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          How a register may be taken, and the percentages that decide who is reported short.
        </p>
      </div>

      {institutions.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <label className="label" style={{ margin: 0 }}>Institution</label>
          <select className="input" value={institutionId} onChange={(e) => setInstitutionId(e.target.value)}
                  style={{ width: 'auto', minWidth: 220 }}>
            {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            These settings apply to this institution only.
          </span>
        </div>
      )}

      {error && (
        <div className="card" style={{ padding: 14, marginBottom: 14, borderLeft: '3px solid var(--danger, #c0392b)' }}>
          {error}
        </div>
      )}

      <div className="card" style={{ padding: 18, marginBottom: 16, maxWidth: 720 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Ways to take a register</div>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 0, marginBottom: 14 }}>
          Switch on as many as you want. Teachers get whichever are on, and can use a different one
          on a different day.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {available.map((m) => {
            const text = MODE_TEXT[m] ?? { label: m, blurb: '' };
            const locked = m === 'NameList';
            // ⚠ Face is offered by the API and NOT switchable here, because nothing implements it
            // yet. A switch that saves cleanly and changes nothing on any screen is worse than no
            // switch: the school believes it is running face attendance and it is not.
            const notYet = m === 'Face';
            const on = locked ? true : modes.includes(m);
            return (
              <label key={m}
                     style={{
                       display: 'flex', gap: 12, alignItems: 'flex-start', padding: 12,
                       borderRadius: 10, border: '1px solid var(--line, #e5e5e5)',
                       opacity: notYet ? 0.65 : 1,
                       cursor: locked || notYet ? 'default' : 'pointer',
                     }}>
                <input type="checkbox" checked={on} disabled={locked || notYet}
                       onChange={() => toggle(m)} style={{ marginTop: 3 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600 }}>{text.label}</span>
                    {locked && <span className="eyebrow" style={{ fontSize: 10 }}>always on</span>}
                    {notYet && <span className="eyebrow" style={{ fontSize: 10 }}>not available yet</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>{text.blurb}</div>
                  {locked && (
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
                      Cannot be switched off — it is what a teacher falls back to when a camera fails,
                      a child has no card, or a photograph was never uploaded.
                    </div>
                  )}
                  {text.needs && (
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
                      {text.needs} · {text.to}
                    </div>
                  )}
                  {notYet && (
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
                      Being built. It will also need each parent’s written consent for their own
                      child — a school-level switch is not enough, and this system will not let one
                      stand in for the other.
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 16, maxWidth: 720 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Percentages</div>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 0, marginBottom: 14 }}>
          These decide who appears in the shortage report and, later, who is exam-eligible.
        </p>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label className="label" style={{ fontSize: 11 }}>Eligible at or above</label>
            <input className="input" type="number" min={0} max={100} value={eligible}
                   onChange={(e) => { setSaved(false); setEligible(Number(e.target.value)); }}
                   style={{ width: 120 }} />
          </div>
          <div>
            <label className="label" style={{ fontSize: 11 }}>Condonation floor</label>
            <input className="input" type="number" min={0} max={100} value={floor}
                   onChange={(e) => { setSaved(false); setFloor(Number(e.target.value)); }}
                   style={{ width: 120 }} />
          </div>
          <div>
            <label className="label" style={{ fontSize: 11 }}>Report short below</label>
            <input className="input" type="number" min={0} max={100} value={shortage}
                   onChange={(e) => { setSaved(false); setShortage(Number(e.target.value)); }}
                   style={{ width: 120 }} />
          </div>
        </div>
        {floorTooHigh && (
          <div style={{ fontSize: 12, color: 'var(--danger, #c0392b)', marginTop: 10 }}>
            The condonation floor cannot be above the eligible threshold — condonation is the band
            below it.
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 16, maxWidth: 720 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Teaching days</div>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 0, marginBottom: 14 }}>
          Days that count towards attendance. Holidays are handled separately, on the calendar.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {DAYS.map((d, i) => (
            <button key={d} type="button" onClick={() => toggleDay(i)}
                    aria-pressed={dayOn(i)}
                    className={`btn ${dayOn(i) ? 'btn-accent' : ''}`}
                    style={{ minWidth: 62 }}>
              {d}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
        <button className="btn btn-accent" disabled={busy || !policy || floorTooHigh} onClick={save}>
          {busy ? 'Saving…' : 'Save settings'}
        </button>
        {saved && <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Saved. The menu now shows what is switched on.</span>}
      </div>

      {others.length > 0 && (
        <div className="card" style={{ padding: 18, maxWidth: 720 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Your other institutions</div>
          <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 0, marginBottom: 12 }}>
            Shown so the whole trust is visible at once, rather than one institution at a time.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {others.map((i) => (
              <div key={i.institutionId} style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{i.name}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                  {i.modes.map((m) => MODE_TEXT[m]?.label ?? m).join(' · ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
